import { Text } from '@linktr.ee/arbor/components/Text';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AuthDivider from '@shared/components/AuthDivider';
import AuthForm from '@shared/components/AuthForm';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import { InputPassword } from '@shared/components/InputPassword';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import { LoginError } from '@shared/components/LoginError';
import {
  getAuthOtpChallengeRequestFailureReasonCode,
  getAuthPasswordVerificationFailureReasonCode,
  publishAuthOtpChallengeRequestCompletedEvent,
  publishAuthOtpChallengeRequestedEvent,
  publishAuthPasswordPromptDisplayedEvent,
  publishAuthPasswordSubmittedEvent,
  publishAuthPasswordVerificationFailedEvent,
  publishLoginAttemptedEvent,
  publishLoginErrorEncounteredEvent,
} from '@shared/utils/analytics/events';
import getPlatformType from '@shared/utils/getPlatformType';
import { isMobileApp } from '@shared/utils/isMobileApp';
import { useIsProfileReferral } from '@shared/utils/useIsProfileReferral';
import bannerLoginImage from '@universal-login/images/banner-login-desktop.webp';
import { PasswordLoginScreenProps } from '@universal-login/types/descope';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

const PasswordLoginScreen: React.FC<PasswordLoginScreenProps> = ({
  onFormUpdate,
  onClickPasswordLogin,
  onClickEmailOTP,
  onEmailOtpChallengeRequested,
  onEmailOtpChallengeRequestFailed,
  onClickEditEmailUsername,
  onEmailOtpAttempted,
  errorText,
  context,
  resetError,
  onClickSignUp,
}) => {
  const { t } = useTranslation();
  const initialEmailOrUsername = context?.form?.['form.emailOrUsername'];
  const [emailOrUsername, setEmailOrUsername] = useState(
    typeof initialEmailOrUsername === 'string' ? initialEmailOrUsername : ''
  );
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [pageError, setPageError] = useState<
    | {
        error?: string;
        description?: string;
        errorDescription?: string;
      }
    | undefined
  >(undefined);
  const isProfileReferral = useIsProfileReferral();
  const platformType = getPlatformType(isProfileReferral, isMobileApp());

  useEffect(() => {
    publishAuthPasswordPromptDisplayedEvent();
  }, []);

  // Handle errors from Descope
  useEffect(() => {
    if (errorText && errorText !== pageError?.error) {
      if (errorText === 'Wrong password or unknown user') {
        setPasswordError('invalid_password');
        publishAuthPasswordVerificationFailedEvent(
          getAuthPasswordVerificationFailureReasonCode(errorText)
        );
      } else {
        setPageError({ error: errorText });
      }
    }
  }, [errorText, pageError?.error]);

  const handlePasswordLogin = async (
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    e?.preventDefault();

    // Clear errors before attempting login
    resetError();
    setPasswordError(undefined);
    setPageError(undefined);

    setLoginLoading(true);
    try {
      publishAuthPasswordSubmittedEvent();
      publishLoginAttemptedEvent('Email', platformType);
      const formData = { password, 'form.emailOrUsername': emailOrUsername };
      onFormUpdate(formData);
      await onClickPasswordLogin(); // Login button interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      publishAuthPasswordVerificationFailedEvent(
        getAuthPasswordVerificationFailureReasonCode(error)
      );
      publishLoginErrorEncounteredEvent('Email', errorMessage);
      setPageError({ error: errorMessage });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleEmailOTPLogin = async (
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    e?.preventDefault();
    setOtpLoading(true);
    const requestTrigger = 'explicit_fallback';

    try {
      onEmailOtpChallengeRequested?.();
      publishAuthOtpChallengeRequestedEvent('login', requestTrigger);
      publishLoginAttemptedEvent('Email OTP', platformType);
      onEmailOtpAttempted?.();
      const formData = { 'form.emailOrUsername': emailOrUsername };
      onFormUpdate(formData);
      await onClickEmailOTP(); // Continue with email OTP interaction, it sends email otp to the user
      publishAuthOtpChallengeRequestCompletedEvent(
        'login',
        requestTrigger,
        'succeeded'
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      publishAuthOtpChallengeRequestCompletedEvent(
        'login',
        requestTrigger,
        'failed',
        getAuthOtpChallengeRequestFailureReasonCode(error)
      );
      onEmailOtpChallengeRequestFailed?.();
      publishLoginErrorEncounteredEvent('Email OTP', errorMessage);
      setPageError({ error: errorMessage });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleEditEmailUsername = () => {
    onClickEditEmailUsername();
  };

  const handlePasswordChange = (value: string) => {
    // Clear errors when user starts typing
    setPasswordError(undefined);
    setPageError(undefined);
    resetError();
    setPassword(value);
    onFormUpdate({ password: value });
  };

  const handleOtpFlow = async () => {
    setPasswordError(undefined);
    setPageError(undefined);
    resetError();
    await handleEmailOTPLogin();
  };

  const isFormValid = emailOrUsername.length > 0 && password.length > 0;

  return (
    <Layout
      heading={t('Welcome back')}
      subHeading={t('Log in to your Linktree')}
      variant="banner"
      bannerImageDesktop={bannerLoginImage}
    >
      <div className="space-y-6">
        <LoginError
          passwordError={passwordError}
          pageError={pageError}
          onTriggerOtpFlow={handleOtpFlow}
        />

        <AuthForm>
          <InputText
            label={t('Email or username')}
            value={emailOrUsername}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmailOrUsername(e.target.value);
              onFormUpdate({ 'form.emailOrUsername': e.target.value });
            }}
            placeholder={t('Email or username')}
            name="emailOrUsername"
            disabled={true}
            showEditButton={true}
            onEdit={handleEditEmailUsername}
            state="disabled"
            autoCapitalize="none"
          />

          <InputPassword
            className="password-login-input"
            label={t('Password')}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handlePasswordChange(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && isFormValid && !loginLoading) {
                e.preventDefault();
                handlePasswordLogin();
              }
            }}
          />

          <Button
            onClick={handlePasswordLogin}
            disabled={!isFormValid}
            label={t('Login')}
            state={loginLoading ? 'loading' : 'default'}
          />
        </AuthForm>

        <AuthDivider />

        <Button
          onClick={handleEmailOTPLogin}
          label={t('Continue with one-time code')}
          state={otpLoading ? 'loading' : 'default'}
        />

        <div className="text-center mt-8">
          <div className="flex justify-center items-center py-6 text-center">
            <AuthLink>
              <a href={`${LINKTREE_BASE_URL}/request`}>
                {t('Forgot password?')}
              </a>
            </AuthLink>
            <span className="mx-1">•</span>
            <AuthLink>
              <a href={`${LINKTREE_BASE_URL}/forgot-username`}>
                {t('Forgot username?')}
              </a>
            </AuthLink>
          </div>
          <Text as="p" variant="body-small-regular" className="text-gray-600">
            {t("Don't have an account?")}{' '}
            <AuthLink>
              <button type="button" onClick={onClickSignUp}>
                {t('Sign up')}
              </button>
            </AuthLink>
          </Text>
        </div>
      </div>
    </Layout>
  );
};

export default PasswordLoginScreen;
